import React, { useEffect, useMemo } from "react";
import { FormHelperText, InputAdornment, SxProps, TextField } from "@mui/material";
import { Controller, FieldError, useFormContext, useWatch } from "react-hook-form";
import { FnComponents, I18n } from "utilities/utilities";
import { I18NextNs, RegularExpression, TypeComponent } from "enum/enum";

export const TextBoxItem = (props: ITextbox) => {
    //#region Variable
    const {
        id,
        name,
        label = "",
        placeholder,
        defaultValue = null,
        pattern,
        maxLength = 200,
        type = "text",
        variant = "outlined",
        size = "small",
        margin = "dense",
        required = false,
        textAlign,
        max = null,
        min = null,
        IsShowMessageError = true,
        disabled = false,
        fullWidth = true,
        startAdornment,
        endAdornment,
        autoComplete = "off",
        IsShrink,
        IsDisplayLabel = true,
        isPassword = false,
        isNotPatternPassword = false,
        isTelNo = false,
        sx,
        IsShowTextCont = true
    } = props;
    const {
        control,
        register,
        getValues,
    } = useFormContext();
    //#endregion

    const setObjValidate = (pattern, objvalidate) => {
        switch (pattern) {
            case "th":
                objvalidate["pattern"] = {
                    value: RegularExpression.Th,
                };
                break;
            case "th-number":
                objvalidate["pattern"] = {
                    value: RegularExpression.ThAndNumber,
                };
                break;
            case "en":
                objvalidate["pattern"] = {
                    value: RegularExpression.En,
                };
                break;
            case "en-number":
                objvalidate["pattern"] = {
                    value: RegularExpression.EnAndNumber,
                };
                break;
        }

        return objvalidate;
    }

    const rules = useMemo(() => {
        let objvalidate = {};
        if (!disabled) {
            objvalidate["required"] = {
                value: required,
            };

            switch (type) {
                case "text":
                    if (isPassword && !isNotPatternPassword) {
                        objvalidate["pattern"] = {
                            value: RegularExpression.Password,
                        };
                    }
                    else if (isTelNo) {
                        objvalidate["pattern"] = {
                            // value: RegularExpression.Number,
                            // value: RegularExpression.EnAndNumber, //ระบบนี้เบอร์โทรต้องกรอกตัวอักษรอังได้
                            value: RegularExpression.TelNo,
                        };
                    }
                    else {
                        objvalidate = setObjValidate(pattern, objvalidate);
                    }
                    break;
                case "email":
                    objvalidate["pattern"] = {
                        value: RegularExpression.Email,
                    };
                    break;
                case "password":
                    if (!isNotPatternPassword) {
                        objvalidate["pattern"] = {
                            value: RegularExpression.Password,
                        };
                    }
                    break;
                case "number":
                    if (!isNotPatternPassword) {
                        objvalidate["pattern"] = {
                            value: RegularExpression.NumberScientific,
                        };
                    }
                    break;
            }
        }
        return objvalidate;
    }, [disabled, required, type, pattern]);

    const genMessagePattern = (type) => {
        let sMessage = "";
        switch (type) {
            case "text":
                if (isPassword) {
                    sMessage = `${I18n.SetText("patternPassword", I18NextNs.validation)}`;
                }
                else if (isTelNo) {
                    sMessage = `${I18n.SetText("patternNumber", I18NextNs.validation)}`;
                }
                else {
                    switch (pattern) {
                        case "th":
                            sMessage = `${I18n.SetText("patternThai", I18NextNs.validation)}`;
                            break;
                        case "th-number":
                            sMessage = `${I18n.SetText("patternThaiNumber", I18NextNs.validation)}`;
                            break;
                        case "en":
                            sMessage = `${I18n.SetText("patternEng", I18NextNs.validation)}`;
                            break;
                        case "en-number":
                            sMessage = `${I18n.SetText("patternEngNumber", I18NextNs.validation)}`;
                            break;
                    }
                }
                break;
            case "email":
                sMessage = `${I18n.SetText("patternEmail", I18NextNs.validation)}`;
                break;
            case "password":
                if (!isNotPatternPassword) sMessage = `${I18n.SetText("patternPassword", I18NextNs.validation)}`;
                break;
        }
        return sMessage;
    }

    const genMessage = (min, max) => {
        let sMessage = "";
        if (min && max) {
            sMessage = `${I18n.SetText("inputMinMax", I18NextNs.validation)} ${min} ${I18n.SetText("to")} ${max} `;
            // sMessage = `สิทธิ์วันลาสมทบไม่เกิน ${max} วัน/ปี`;
        }
        else if (min && !max) {
            sMessage = `${I18n.SetText("inputMin", I18NextNs.validation)} ${min}`;
            // sMessage = `สิทธิ์วันลาสมทบไม่เกิน ${max} วัน/ปี`;

        }
        else if (!min && max) {
            // sMessage = `${I18n.SetText("inputMax", I18NextNs.validation)} ${max} `;
            sMessage = `สิทธิ์วันลาสมทบไม่เกิน ${max} วัน/ปี`;
        }
        return sMessage;
    }

    const getMessageValidate = (error: FieldError) => {
        let sMessage = "";
        if (IsShowMessageError) {
            if (error) {
                switch (error.type) {
                    case "required":
                        sMessage = `${I18n.SetText("required", I18NextNs.validation)} ${label}`;
                        break;
                    case "pattern":
                        sMessage = genMessagePattern(type);
                        break;
                    case "value":
                        sMessage = error.message;
                        break;
                    case "validate":
                        sMessage = genMessage(min, max);
                        break;
                }
            }
        } else {
            if (error) {
                switch (error.type) {
                    case "validate":
                        sMessage = genMessage(min, max);
                        break;
                }
            }
        }

        return sMessage;
    };

    const FieldName = useWatch({
        control,
        name: name,
    });

    const [focus, setFocus] = React.useState(false);
    const sCounter = `"${getValues(name) ? getValues(name).length : 0}/${props.maxLength}"`;
    const maxLengthText: SxProps = {
        "::after": {
            content: IsShowTextCont && props.maxLength
                ? sCounter : '""',
            position: "absolute",
            bottom: "5px",
            right: "10px",
            color: "#989898",
            fontSize: "10px",
        },
    };

    return (
        <Controller
            key={FnComponents.GetKey(TypeComponent.input, name)}
            name={name}
            control={control}
            rules={rules}
            shouldUnregister={true}
            defaultValue={defaultValue}
            render={({
                field: { onChange, ref, value },
                fieldState: { invalid, error }
            }) => {
                let helperText = IsShowMessageError ? getMessageValidate(error) : "";
                let IsValid = invalid && !disabled;
                // let InputLabelProps = {};
                let sValue = value ? value + "" : "";
                // if (IsShrink || sValue.length > 0 || FieldName || focus) {
                //     InputLabelProps["shrink"] = true;
                // }
                return (
                    <React.Fragment>
                        <TextField
                            sx={[{ ...maxLengthText }, sx]}
                            {...register(name)}
                            required={required}
                            id={FnComponents.GetId(TypeComponent.input, id, name)}
                            name={name}
                            inputRef={ref}
                            label={FnComponents.GetLabel(label, IsDisplayLabel)}
                            placeholder={placeholder}
                            variant={variant}
                            type={type}
                            error={IsValid}
                            size={size}
                            fullWidth={fullWidth}
                            autoComplete={autoComplete ?? "off"}
                            // margin={margin}
                            inputProps={{ maxLength: maxLength }}
                            InputProps={{
                                startAdornment: startAdornment,
                                endAdornment: endAdornment,
                            }}
                            // InputLabelProps={InputLabelProps}
                            InputLabelProps={{
                                shrink: IsShrink || sValue.length > 0 || FieldName || focus,
                            }}
                            disabled={disabled}
                            onBlur={(e) => {
                                if (!IsShrink) setFocus(false);
                                if (props.onBlur) {
                                    props.onBlur(e);
                                }
                            }}
                            onChange={(e) => {
                                if (props.onChange) {
                                    props.onChange(e);
                                }
                                onChange(e);
                            }}

                            onClick={(e) => {
                                if (props.onClick) {
                                    props.onClick(e);
                                }
                            }}
                            style={props.style}
                            onKeyDown={(e) => {
                                if (e.code === "Space") {
                                    e.stopPropagation()
                                }
                            }}
                            onFocus={() => {
                                if (!IsShrink)setFocus(true);
                            }}
                            onKeyPress={(e) => {
                                if (props.onKeyPress) props.onKeyPress(e);
                            }}
                        />
                        {IsShowMessageError && IsValid && (
                            <FormHelperText>{helperText}</FormHelperText>
                        )}
                    </React.Fragment>
                );
            }}
        />
    );
};